import type { BatchActionItem } from '@/components/ui/workflow';
import type { InstitutionWarning, NotificationStats } from './types';
import type {
  WorkflowInstitution,
  WorkflowDocument
} from '@/lib/services/correspondences/workflows/workflow-types';
import { INSTITUTION_BRANDS } from './constants';

export const calculateStats = (
  items: BatchActionItem[]
): NotificationStats => ({
  total: items.length,
  pending: items.filter((item) => item.status === 'pending').length,
  sent: items.filter((item) => item.status === 'success').length,
  failed: items.filter((item) => item.status === 'error').length
});

export const calculateProgress = (stats: NotificationStats): number =>
  stats.total > 0 ? (stats.sent / stats.total) * 100 : 0;

/**
 * Check for missing documents using backend data structure
 */
export const checkMissingDocuments = (
  institution: WorkflowInstitution,
  availableDocuments: WorkflowDocument[]
): string[] => {
  const missingDocs: string[] = [];

  // Only check for institution-specific documents
  // Death certificate is matter-wide and handled separately

  // Check if Will is required and available (e.g., for NAB)
  if (institution.name.includes('NAB')) {
    const hasWill = availableDocuments.some((doc) => doc.fileType === 'will');
    if (!hasWill) {
      missingDocs.push('Will');
    }
  }

  return missingDocs;
};

/**
 * Check for missing matter-wide documents (death certificate, etc.)
 */
export const checkMissingMatterDocuments = (
  availableDocuments: WorkflowDocument[]
): string[] => {
  const missingDocs: string[] = [];

  // Check if Death Certificate is available
  const hasDeathCertificate = availableDocuments.some(
    (doc) => doc.fileType === 'death-certificate' && doc.isCertifiedCopy
  );
  if (!hasDeathCertificate) {
    missingDocs.push('Death Certificate');
  }

  return missingDocs;
};

/**
 * Get warnings for an institution using backend data
 */
export const getInstitutionWarnings = (
  institution: WorkflowInstitution,
  availableDocuments?: WorkflowDocument[]
): InstitutionWarning[] => {
  const warnings: InstitutionWarning[] = [];

  // Check for missing documents if documents are provided
  if (availableDocuments) {
    const missingDocs = checkMissingDocuments(institution, availableDocuments);

    // Filter out "Will" from warnings (it's shown as a badge)
    const filteredMissingDocs = missingDocs.filter(
      (doc) => doc.toLowerCase() !== 'will'
    );

    if (filteredMissingDocs.length > 0) {
      warnings.push({
        type: 'missing-docs',
        message: `Missing: ${filteredMissingDocs.join(', ')}`
      });
    }
  }

  return warnings;
};

/**
 * Calculate total number of assets across institutions
 */
export const getTotalAssets = (institutions: WorkflowInstitution[]): number =>
  institutions.reduce((sum, inst) => sum + (inst.inventory?.length || 0), 0);

/**
 * Calculate total value for an institution
 */
export const getTotalValue = (institution: WorkflowInstitution): number =>
  institution.inventory?.reduce((sum, item) => sum + (item.balance || 0), 0) ||
  0;

/**
 * Get branding for institution
 */
export const getInstitutionBranding = (institutionName: string) => {
  // Check for exact matches first
  if (INSTITUTION_BRANDS[institutionName as keyof typeof INSTITUTION_BRANDS]) {
    return INSTITUTION_BRANDS[
      institutionName as keyof typeof INSTITUTION_BRANDS
    ];
  }

  // Check for partial matches (case-insensitive)
  const lowerName = institutionName.toLowerCase();
  for (const [brand, styling] of Object.entries(INSTITUTION_BRANDS)) {
    if (
      lowerName.includes(brand.toLowerCase()) ||
      brand.toLowerCase().includes(lowerName)
    ) {
      return styling;
    }
  }

  // Return default styling if no match found
  return INSTITUTION_BRANDS.default;
};
